import type { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import CodeMirror, { type BasicSetupOptions, type ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { type FC, type Ref, useMemo } from 'react';
import { RADIUS_ITEM } from 'styles/tokens';

import { editorTheme, searchMatchCounter } from './codeMirrorTheme';

const ERROR_RED = '#E7233B';

// Red inset ring when the field is in an error state (e.g. invalid JSON). Stable singleton
// so it isn't re-allocated on every render.
const errorTheme = EditorView.theme({
  '&': { boxShadow: `inset 0 0 0 2px ${alpha(ERROR_RED, 0.5)}` },
  '&.cm-focused': { boxShadow: `inset 0 0 0 2px ${alpha(ERROR_RED, 0.7)}` },
});

const DEFAULT_BASIC_SETUP: BasicSetupOptions = {
  lineNumbers: true,
  foldGutter: false,
  highlightActiveLine: true,
  highlightActiveLineGutter: true,
  searchKeymap: true,
  bracketMatching: false,
  closeBrackets: false,
  autocompletion: false,
};

export interface CodeMirrorEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** Extra CodeMirror extensions concatenated after the shared defaults (theme, wrapping, search). */
  extensions?: Extension[];
  /** Overrides merged onto the default basicSetup (line numbers, search keymap, etc.). */
  basicSetup?: BasicSetupOptions;
  ariaLabel?: string;
  placeholder?: string;
  readOnly?: boolean;
  /** Focus the editor on mount (used when entering edit mode). */
  autoFocus?: boolean;
  /** Show the red error ring (validation failures surfaced by the consumer). */
  error?: boolean;
  /** Fixed editor height (e.g. "100%", 240). When set, the editor fills it and scrolls inside. */
  height?: string | number;
  minHeight?: string | number;
  maxHeight?: string | number;
  /** Ref to the CodeMirror instance, exposing `.view` for cursor-aware edits. */
  editorRef?: Ref<ReactCodeMirrorRef>;
}

/**
 * Generic editor built on CodeMirror 6, styled to the Relif design system (no 1px borders,
 * tonal surface, Navy text, inset focus ring, glassmorphism search panel). Provides line
 * numbers, soft line wrapping and in-editor search (Cmd/Ctrl+F) out of the box. Specialised
 * editors (prompt-tag highlighting, JSON) layer extra extensions on top via `extensions`.
 */
const CodeMirrorEditor: FC<CodeMirrorEditorProps> = ({
  value,
  onChange,
  extensions,
  basicSetup,
  ariaLabel,
  placeholder,
  readOnly,
  autoFocus,
  error = false,
  height,
  minHeight,
  maxHeight,
  editorRef,
}) => {
  const mergedExtensions = useMemo<Extension[]>(
    () => [
      EditorView.lineWrapping,
      searchMatchCounter,
      editorTheme,
      ...(extensions ?? []),
      ...(error ? [errorTheme] : []),
    ],
    [extensions, error]
  );

  const mergedBasicSetup = useMemo<BasicSetupOptions>(() => ({ ...DEFAULT_BASIC_SETUP, ...basicSetup }), [basicSetup]);

  return (
    <Box
      sx={{
        // Fill the parent (which owns the height) and scroll inside it.
        // Chain: this box → @uiw wrapper div → .cm-editor.
        height: height === '100%' ? '100%' : 'auto',
        width: '100%',
        overflow: 'hidden',
        borderRadius: RADIUS_ITEM,
        '& > div': height === '100%' ? { height: '100%' } : undefined,
        '& .cm-editor': height === '100%' ? { height: '100%' } : undefined,
      }}
    >
      <CodeMirror
        ref={editorRef}
        value={value}
        height={typeof height === 'number' ? `${height}px` : height}
        minHeight={typeof minHeight === 'number' ? `${minHeight}px` : minHeight}
        maxHeight={typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight}
        autoFocus={autoFocus}
        readOnly={readOnly}
        placeholder={placeholder}
        onChange={onChange}
        extensions={mergedExtensions}
        basicSetup={mergedBasicSetup}
        aria-label={ariaLabel}
      />
    </Box>
  );
};

export default CodeMirrorEditor;
